import React from 'react';
import '../../styles/Pagination.css';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({ 
  currentPage, 
  totalPages, 
  onPageChange,
  disabled = false
}) => {
  // Generate array of page numbers to display
  const getPageNumbers = () => {
    const pageNumbers = [];
    
    // Always show first page
    pageNumbers.push(1);
    
    // Calculate range to show around current page
    const leftBound = Math.max(2, currentPage - 1);
    const rightBound = Math.min(totalPages - 1, currentPage + 1);
    
    // Add ellipsis after first page if needed
    if (leftBound > 2) {
      pageNumbers.push('...');
    }
    
    // Add pages around current page
    for (let i = leftBound; i <= rightBound; i++) {
      pageNumbers.push(i);
    }
    
    // Add ellipsis before last page if needed
    if (rightBound < totalPages - 1) {
      pageNumbers.push('...');
    }
    
    // Always show last page if there is more than one page
    if (totalPages > 1) {
      pageNumbers.push(totalPages);
    }
    
    return pageNumbers;
  };

  if (totalPages <= 1) {
    return null; // Don't show pagination if there's only one page
  }

  return (
    <div className={`pagination ${disabled ? 'disabled' : ''}`}>
      <button 
        className="pagination-button" 
        onClick={() => !disabled && onPageChange(currentPage - 1)}
        disabled={currentPage === 1 || disabled}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
      </button>
      
      <div className="pagination-numbers">
        {getPageNumbers().map((page, index) => (
          page === '...' ? (
            <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
          ) : (
            <button
              key={`page-${page}`}
              className={`pagination-number ${currentPage === page ? 'active' : ''}`}
              onClick={() => !disabled && typeof page === 'number' && onPageChange(page)}
              disabled={disabled}
            >
              {page}
            </button>
          )
        ))}
      </div>
      
      <button 
        className="pagination-button" 
        onClick={() => !disabled && onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages || disabled}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </button>
    </div>
  );
};

export default Pagination; 